import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Form, Button, Alert, Accordion } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import { selectAuthUserId } from '../redux/authSlice';
import { userInstructionsService } from '../services/memory';
import { AGENT_PERSONAS } from '../config/agentPersonas';
import FancyButton from '../components/ui/fancyButton';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const AgentPersonasPreferences = () => {
  const userId = useSelector(selectAuthUserId);
  const [instructions, setInstructions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [charCounts, setCharCounts] = useState({});

  const loadInstructions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      await userInstructionsService.initialize(userId);
      const allInstructions = userInstructionsService.getAll();
      setInstructions(allInstructions);

      // Initialize character counts
      const counts = {};
      Object.keys(allInstructions).forEach((agentId) => {
        counts[agentId] = allInstructions[agentId]?.length || 0;
      });
      setCharCounts(counts);
    } catch (err) {
      setError('Failed to load agent customizations');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadInstructions();
    }
  }, [userId, loadInstructions]);

  const handleInstructionChange = (agentId, value) => {
    // Enforce 100 character limit
    const truncated = value.slice(0, 100);

    setInstructions((prev) => ({
      ...prev,
      [agentId]: truncated,
    }));

    setCharCounts((prev) => ({
      ...prev,
      [agentId]: truncated.length,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      // Save each agent's instructions
      for (const [agentId, instruction] of Object.entries(instructions)) {
        if (instruction) {
          await userInstructionsService.set(userId, agentId, instruction);
        }
      }

      setMessage('Agent customizations saved successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError('Failed to save agent customizations');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (agentId) => {
    try {
      await userInstructionsService.reset(userId, agentId);
      const defaultInstruction = userInstructionsService.get(agentId);

      setInstructions((prev) => ({
        ...prev,
        [agentId]: defaultInstruction,
      }));

      setCharCounts((prev) => ({
        ...prev,
        [agentId]: defaultInstruction.length,
      }));

      setMessage(`${AGENT_PERSONAS[agentId]?.name || agentId} reset to default!`);
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setError('Failed to reset agent');
    }
  };

  const handleResetAll = async () => {
    if (!window.confirm('Reset all agents to default customizations?')) return;

    try {
      const resetInstructions = {};
      const resetCounts = {};

      for (const agentId of Object.keys(instructions)) {
        await userInstructionsService.reset(userId, agentId);
        const defaultInstruction = userInstructionsService.get(agentId);
        resetInstructions[agentId] = defaultInstruction;
        resetCounts[agentId] = defaultInstruction.length;
      }

      setInstructions(resetInstructions);
      setCharCounts(resetCounts);
      setMessage('All agents reset to defaults!');
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setError('Failed to reset all agents');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Container className="py-5 agent-personas-page">
      <div className="mb-4">
        <h1 className="mb-2">
          <i className="fa-solid fa-user-gear me-3" aria-hidden="true" />
          Agent Customization
        </h1>
        <p className="text-muted">
          Personalize how each AI agent behaves with free-form instructions. Tell them your
          preferences for tone, style, and approach in your own words (max 100 characters).
        </p>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {message && (
        <Alert variant="success" dismissible onClose={() => setMessage(null)}>
          {message}
        </Alert>
      )}

      <Accordion defaultActiveKey="professor" className="mb-4">
        {Object.entries(AGENT_PERSONAS).map(([agentId, agentConfig]) => (
          <Accordion.Item eventKey={agentId} key={agentId}>
            <Accordion.Header>
              <div className="d-flex align-items-center w-100">
                <i className={`fa-solid ${agentConfig.icon} me-3 fs-4`} aria-hidden="true" />
                <div>
                  <div className="fw-bold">{agentConfig.name}</div>
                  <small className="text-muted">{agentConfig.description}</small>
                </div>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <div className="mb-3">
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Custom Instructions
                    <span className="ms-2 text-muted small">
                      ({charCounts[agentId] || 0}/100 characters)
                    </span>
                  </Form.Label>
                  <Form.Text className="d-block mb-2 text-muted">
                    Tell this agent how you want it to behave. Be specific about tone, style, and
                    approach.
                  </Form.Text>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={instructions[agentId] || ''}
                    onChange={(e) => handleInstructionChange(agentId, e.target.value)}
                    placeholder={`e.g., "Be concise and direct. Focus on actionable advice. Use technical terminology."`}
                    maxLength={100}
                  />
                  {charCounts[agentId] >= 95 && (
                    <Form.Text className="text-warning">
                      <i className="fa-solid fa-exclamation-triangle me-1" />
                      Approaching character limit
                    </Form.Text>
                  )}
                </Form.Group>

                <div className="alert alert-info small mb-0">
                  <strong>
                    <i className="fa-solid fa-lightbulb me-2" />
                    Example:
                  </strong>
                  {agentId === 'professor' &&
                    ' "Explain concepts simply with real-world examples. Be encouraging."'}
                  {agentId === 'examiner' &&
                    ' "Be strict but fair. Provide detailed feedback after exams."'}
                  {agentId === 'librarian' &&
                    ' "Recommend focused, essential resources. Organize clearly."'}
                  {agentId === 'dashboard' &&
                    ' "Give brief highlights. Celebrate wins. Keep it motivating."'}
                  {agentId === 'tasks' &&
                    ' "Be direct and action-oriented. Help me prioritize ruthlessly."'}
                  {agentId === 'reflection' &&
                    ' "Ask deep questions. Help me discover insights on my own."'}
                  {agentId === 'courseBrowse' &&
                    ' "Be enthusiastic about options. Give detailed comparisons."'}
                  {agentId === 'orchestrator' &&
                    ' "Be efficient. Show me what you\'re doing step-by-step."'}
                </div>
              </div>

              <div className="d-flex justify-content-end">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleReset(agentId)}
                  disabled={saving}
                >
                  <i className="fa-solid fa-rotate-left me-2" aria-hidden="true" />
                  Reset to Default
                </Button>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        ))}
      </Accordion>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <Button
                variant="outline-danger"
                onClick={handleResetAll}
                disabled={saving}
                className="me-2"
              >
                <i className="fa-solid fa-rotate-left me-2" aria-hidden="true" />
                Reset All to Defaults
              </Button>
            </div>
            <div>
              <FancyButton onClick={handleSave} disabled={saving}>
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-save me-2" aria-hidden="true" />
                    Save Preferences
                  </>
                )}
              </FancyButton>
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="mt-4 border-0 bg-light">
        <Card.Body>
          <h5 className="mb-3">
            <i className="fa-solid fa-lightbulb me-2 text-warning" aria-hidden="true" />
            How This Works
          </h5>
          <ul className="mb-0">
            <li className="mb-2">
              <strong>Free-form customization:</strong> Write instructions in your own words instead
              of selecting from predefined options
            </li>
            <li className="mb-2">
              <strong>100 character limit:</strong> Keep instructions concise and focused on what
              matters most
            </li>
            <li className="mb-2">
              <strong>Immediate effect:</strong> Changes apply to all future conversations after
              saving
            </li>
          </ul>
          <p className="mt-3 mb-0 text-muted small">
            Your customizations are stored securely and persist across all sessions. Each agent can
            have unique instructions tailored to your preferences.
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AgentPersonasPreferences;
